import { objectEntries } from "@redotech/util/object";
import { WhereConditionDataType } from "../../segments/segment-where-condition";
import {
  CustomerActivityType,
  CustomerCharacteristicType,
  CustomerDataSource,
  SegmentConditionCategory,
} from "./segment-types";

/**
 * Defines data that customer activities may have on them.
 *
 * e.g. an OrderCreated event may have a numeric total order value field
 * A ProductAddedToCart may have a token collection field of the product.
 *
 * Also is reused to define data on the customer itself.
 *
 */
export interface SegmentDataStructure<T extends string> {
  fields: Record<T, SegmentDataStructureField<T>>;
  /** Where the data comes from. Every field in a structure shares it. */
  source: CustomerDataSource;
}

/**
 * Presentational unit for a numeric field. The stored condition is the same
 * bare number either way.
 */
export type SegmentFieldUnit = "percent" | "currency";

export interface SegmentDataStructureField<T extends string> {
  name: string;
  description: string;
  source: CustomerDataSource;
  dataType: WhereConditionDataType;
  category: SegmentConditionCategory;
  type: T;
  baseFieldName?: string;
  unit?: SegmentFieldUnit;
}

/** The one namespace of dimensions that describes the customer rather than
 *  something they did. */
export const CUSTOMER_CHARACTERISTIC_DATA_STRUCTURE = "customer-characteristic";

export type SegmentDataStructureType =
  | typeof CUSTOMER_CHARACTERISTIC_DATA_STRUCTURE
  | CustomerActivityType;

type FieldConfig<T extends string> = Omit<
  SegmentDataStructureField<T>,
  "source" | "type"
>;

/**
 * Keeps each field's own declaration rather than widening it, so a dimension's
 * data type is known at the type level and not only at runtime.
 */
function createDataStructure<F extends Record<string, FieldConfig<string>>>(
  fields: F,
  source: CustomerDataSource,
): {
  fields: {
    [K in keyof F & string]: F[K] & { type: K; source: CustomerDataSource };
  };
  source: CustomerDataSource;
} {
  const result = {} as {
    [K in keyof F & string]: F[K] & { type: K; source: CustomerDataSource };
  };
  for (const key of Object.keys(fields) as (keyof F & string)[]) {
    result[key] = {
      ...fields[key],
      type: key,
      source,
    } as (typeof result)[typeof key];
  }
  return { fields: result, source };
}

const customerAttributeFields = {
  [CustomerCharacteristicType.CUSTOMER_TAGS]: {
    name: "Customer tags",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "Tags associated with the customer",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.CREATED_TIME]: {
    name: "Created time",
    dataType: WhereConditionDataType.DATE_FULL,
    description: "Time when customer was created",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.BIRTHDAY]: {
    name: "Birthday",
    dataType: WhereConditionDataType.DATE_ANNUAL,
    description: "Birthday of the customer",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.SUBSCRIBED_TO_EMAIL]: {
    name: "Is subscribed to email marketing",
    dataType: WhereConditionDataType.BOOLEAN,
    description: "Is the person subscribed to email marketing?",
    category: SegmentConditionCategory.MARKETING,
  },
  [CustomerCharacteristicType.CAN_RECEIVE_EMAIL_MARKETING]: {
    name: "Is contactable via email",
    dataType: WhereConditionDataType.BOOLEAN,
    description:
      "Can the person receive email marketing? Includes any customer with a defined email who hasn't unsubscribed.",
    category: SegmentConditionCategory.MARKETING,
  },
  [CustomerCharacteristicType.SUBSCRIBED_TO_SMS]: {
    name: "Is subscribed to sms marketing",
    dataType: WhereConditionDataType.BOOLEAN,
    description: "Is the person subscribed to SMS marketing?",
    category: SegmentConditionCategory.MARKETING,
  },
  [CustomerCharacteristicType.STATIC_SEGMENT_MEMBERSHIP]: {
    name: "Static segment membership",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "Is the person a member of a static segment?",
    category: SegmentConditionCategory.MARKETING,
  },
  [CustomerCharacteristicType.RETURN_RATE_COUNT]: {
    name: "Return rate by count",
    dataType: WhereConditionDataType.NUMERIC,
    description:
      "The number of customer returns over the number of customer orders\n\nThis is a number between 0 and 100",
    category: SegmentConditionCategory.SHOPPING,
    unit: "percent",
  },
  [CustomerCharacteristicType.RETURN_RATE_VALUE]: {
    name: "Return rate by value",
    dataType: WhereConditionDataType.NUMERIC,
    description:
      "The total value of returned goods over the total value of the customer's orders\n\nThis is a number between 0 and 100",
    category: SegmentConditionCategory.SHOPPING,
    unit: "percent",
  },
  [CustomerCharacteristicType.LIFETIME_REVENUE]: {
    name: "Lifetime revenue",
    dataType: WhereConditionDataType.NUMERIC,
    description:
      "The total the customer has spent across all their orders, net of refunds and excluding cancelled orders\n\nConverted to your store's currency",
    category: SegmentConditionCategory.SHOPPING,
    unit: "currency",
  },
  [CustomerCharacteristicType.COUNTRY]: {
    name: "Country",
    dataType: WhereConditionDataType.TOKEN,
    description: "The country the person is in",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.STATE_PROVINCE]: {
    name: "State",
    dataType: WhereConditionDataType.TOKEN_HIERARCHY,
    description: "The state or province the person is in",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.CITY]: {
    name: "City",
    dataType: WhereConditionDataType.TOKEN_HIERARCHY,
    description: "The city the person is in",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.PROXIMITY_TO_CITY]: {
    name: "Distance to city",
    dataType: WhereConditionDataType.CITY_PROXIMITY,
    description: "Distance to the city",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.CUSTOM_FIELDS]: {
    name: "Custom field",
    dataType: WhereConditionDataType.CUSTOM_FIELD,
    description: "Custom fields defined for the customer",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.PHONE_NUMBER_AREA_CODE]: {
    name: "Area code",
    dataType: WhereConditionDataType.TOKEN,
    description: "The area code of the customer's phone number",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.ENROLLED_IN_LOYALTY_LION]: {
    name: "Is enrolled in LoyaltyLion loyalty",
    dataType: WhereConditionDataType.BOOLEAN,
    description: "Is the customer enrolled in LoyaltyLion?",
    category: SegmentConditionCategory.LOYALTY_LION,
  },
  [CustomerCharacteristicType.EMAIL_ADDRESS]: {
    name: "Email address",
    dataType: WhereConditionDataType.STRING,
    description: "The customer's email address",
    category: SegmentConditionCategory.ACCOUNT,
  },
  [CustomerCharacteristicType.CUSTOMER_NAME]: {
    name: "Customer name",
    dataType: WhereConditionDataType.STRING,
    description: "The customer's name",
    category: SegmentConditionCategory.ACCOUNT,
  },
} satisfies Record<
  CustomerCharacteristicType,
  FieldConfig<CustomerCharacteristicType>
>;

export const CustomerAttributeDataStructure = createDataStructure(
  customerAttributeFields,
  CustomerDataSource.REDO,
) satisfies SegmentDataStructure<CustomerCharacteristicType>;

function withoutFields<T extends string>(
  fields: Record<T, FieldConfig<T>>,
  exclude: T[] | undefined,
): Record<string, FieldConfig<string>> {
  const remaining = { ...fields };
  for (const key of exclude ?? []) {
    delete remaining[key];
  }
  return remaining as Record<string, FieldConfig<string>>;
}

function createPlatformDataStructures<T extends string>(
  fields: Record<T, FieldConfig<T>>,
  exclude?: {
    fromCommentSold: T[] | undefined;
    fromBigCommerce: T[] | undefined;
  },
): {
  shopify: SegmentDataStructure<T>;
  commentSold: SegmentDataStructure<string>;
  bigCommerce: SegmentDataStructure<string>;
} {
  const shopify = createDataStructure(fields, CustomerDataSource.SHOPIFY);
  const commentSold = createDataStructure(
    withoutFields(fields, exclude?.fromCommentSold),
    CustomerDataSource.COMMENTSOLD,
  );
  const bigCommerce = createDataStructure(
    withoutFields(fields, exclude?.fromBigCommerce),
    CustomerDataSource.BIGCOMMERCE,
  );

  return { shopify, commentSold, bigCommerce };
}

const commonProductFields = {
  productId: (context: string): FieldConfig<string> => ({
    name: "Product id",
    dataType: WhereConditionDataType.TOKEN,
    description: `The id of the product ${context}`,
    category: SegmentConditionCategory.SHOPPING,
  }),
  productName: (context: string): FieldConfig<string> => ({
    name: "Product name",
    dataType: WhereConditionDataType.TOKEN,
    description: `The name of the product ${context}`,
    category: SegmentConditionCategory.SHOPPING,
  }),
  variantId: (context: string): FieldConfig<string> => ({
    name: "Product variant id",
    dataType: WhereConditionDataType.TOKEN,
    description: `The id of the product variant ${context}`,
    category: SegmentConditionCategory.SHOPPING,
  }),
  variantName: (context: string): FieldConfig<string> => ({
    name: "Product variant name",
    dataType: WhereConditionDataType.TOKEN,
    description: `The name of the product variant ${context}`,
    category: SegmentConditionCategory.SHOPPING,
  }),
  variantSku: (context: string): FieldConfig<string> => ({
    name: "Product variant SKU",
    dataType: WhereConditionDataType.TOKEN,
    description: `The SKU of the product variant ${context}`,
    category: SegmentConditionCategory.SHOPPING,
  }),
  collectionName: (context: string): FieldConfig<string> => ({
    name: "Collection names",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: `The names of the collections ${context}`,
    category: SegmentConditionCategory.SHOPPING,
  }),
  productTags: (context: string): FieldConfig<string> => ({
    name: "Product tags",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: `The tags on the product ${context}`,
    category: SegmentConditionCategory.SHOPPING,
  }),
  vendor: (context: string): FieldConfig<string> => ({
    name: "Vendor",
    dataType: WhereConditionDataType.TOKEN,
    description: `The vendor of the product ${context}`,
    category: SegmentConditionCategory.SHOPPING,
  }),
};

export enum ProductViewedSegmentFields {
  PRODUCT_ID = "product_id",
  PRODUCT_VARIANT_ID = "product_variant_id",
  PRODUCT_NAME = "product_name",
  PRODUCT_VARIANT_NAME = "product_variant_name",
  PRODUCT_VARIANT_SKU = "product_variant_sku",
  COLLECTION_NAME = "collection_name",
  PRODUCT_TAGS = "product_tags",
}

const productViewedFields = {
  [ProductViewedSegmentFields.PRODUCT_ID]: {
    name: "Product id",
    dataType: WhereConditionDataType.TOKEN,
    description: "The id of the product that was viewed",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductViewedSegmentFields.PRODUCT_VARIANT_ID]: {
    name: "Product variant id",
    dataType: WhereConditionDataType.TOKEN,
    description: "The id of the product variant that was viewed",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductViewedSegmentFields.PRODUCT_NAME]: {
    name: "Product name",
    dataType: WhereConditionDataType.TOKEN,
    description: "The name of the product that was viewed",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductViewedSegmentFields.PRODUCT_VARIANT_NAME]: {
    name: "Product variant name",
    dataType: WhereConditionDataType.TOKEN,
    description: "The name of the product variant that was viewed",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductViewedSegmentFields.PRODUCT_VARIANT_SKU]: {
    name: "Product variant SKU",
    dataType: WhereConditionDataType.TOKEN,
    description: "The SKU of the product variant that was viewed",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductViewedSegmentFields.COLLECTION_NAME]: {
    name: "Collection names",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The names of the collections that the product belongs to",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductViewedSegmentFields.PRODUCT_TAGS]: {
    name: "Product tags",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The tags on the product",
    category: SegmentConditionCategory.SHOPPING,
  },
} satisfies Record<
  ProductViewedSegmentFields,
  FieldConfig<ProductViewedSegmentFields>
>;

const {
  shopify: ProductViewedShopifyDataStructure,
  commentSold: ProductViewedCommentSoldDataStructure,
} = createPlatformDataStructures(productViewedFields, {
  fromCommentSold: [ProductViewedSegmentFields.PRODUCT_VARIANT_NAME],
  fromBigCommerce: undefined,
});

export enum ProductAddedToCartSegmentFields {
  PRODUCT_NAME = "product_name",
  PRODUCT_VARIANT_NAME = "product_variant_name",
  PRODUCT_ID = "product_id",
  PRODUCT_VARIANT_ID = "product_variant_id",
  PRODUCT_VARIANT_SKU = "product_variant_sku",
  PRODUCT_COLLECTION = "product_collection",
  PRODUCT_TAGS = "product_tags",
  QUANTITY = "quantity",
  CART_SUBTOTAL = "cart_subtotal",
}

const productAddedToCartFields = {
  [ProductAddedToCartSegmentFields.PRODUCT_NAME]: {
    name: "Product name",
    dataType: WhereConditionDataType.TOKEN,
    description: "The name of the product that was added to cart",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductAddedToCartSegmentFields.PRODUCT_VARIANT_NAME]: {
    name: "Product variant name",
    dataType: WhereConditionDataType.TOKEN,
    description: "The name of the product variant that was added to cart",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductAddedToCartSegmentFields.PRODUCT_ID]: {
    name: "Product id",
    dataType: WhereConditionDataType.TOKEN,
    description: "The id of the product that was added to cart",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductAddedToCartSegmentFields.PRODUCT_VARIANT_ID]: {
    name: "Product variant id",
    dataType: WhereConditionDataType.TOKEN,
    description: "The id of the product variant that was added to cart",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductAddedToCartSegmentFields.PRODUCT_VARIANT_SKU]: {
    name: "Product variant SKU",
    dataType: WhereConditionDataType.TOKEN,
    description: "The SKU of the product variant that was added to cart",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductAddedToCartSegmentFields.PRODUCT_COLLECTION]: {
    name: "Product collections",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The collections the added product belongs to",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductAddedToCartSegmentFields.PRODUCT_TAGS]:
    commonProductFields.productTags("that was added to cart"),
  [ProductAddedToCartSegmentFields.QUANTITY]: {
    name: "Item quantity",
    dataType: WhereConditionDataType.NUMERIC,
    description: "The quantity of the product variant that was added to cart",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ProductAddedToCartSegmentFields.CART_SUBTOTAL]: {
    name: "Cart subtotal",
    dataType: WhereConditionDataType.NUMERIC,
    description: "The subtotal value of the cart when the product was added",
    category: SegmentConditionCategory.SHOPPING,
    unit: "currency",
  },
} satisfies Record<
  ProductAddedToCartSegmentFields,
  FieldConfig<ProductAddedToCartSegmentFields>
>;

const {
  shopify: ProductAddedToCartShopifyDataStructure,
  commentSold: ProductAddedToCartCommentSoldDataStructure,
  bigCommerce: ProductAddedToCartBigCommerceDataStructure,
} = createPlatformDataStructures(productAddedToCartFields, {
  fromCommentSold: [ProductAddedToCartSegmentFields.PRODUCT_VARIANT_NAME],
  fromBigCommerce: [
    ProductAddedToCartSegmentFields.PRODUCT_COLLECTION,
    ProductAddedToCartSegmentFields.PRODUCT_TAGS,
  ],
});

export enum CollectionViewedSegmentFields {
  COLLECTION_ID = "collection_id",
  COLLECTION_NAME = "collection_name",
}

const collectionViewedFields = {
  [CollectionViewedSegmentFields.COLLECTION_ID]: {
    name: "Collection id",
    dataType: WhereConditionDataType.TOKEN,
    description: "The id of the collection that was viewed",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CollectionViewedSegmentFields.COLLECTION_NAME]: {
    name: "Collection name",
    dataType: WhereConditionDataType.TOKEN,
    description: "The name of the collection that was viewed",
    category: SegmentConditionCategory.SHOPPING,
  },
} satisfies Record<
  CollectionViewedSegmentFields,
  FieldConfig<CollectionViewedSegmentFields>
>;

const {
  shopify: CollectionViewedShopifyDataStructure,
  commentSold: CollectionViewedCommentSoldDataStructure,
} = createPlatformDataStructures(collectionViewedFields);

export enum ReceivedEmailSegmentFields {
  CAMPAIGN = "campaign",
  AUTOMATION = "automation",
}
export enum OpenedEmailSegmentFields {
  CAMPAIGN = "campaign",
  AUTOMATION = "automation",
}
export enum ClickedEmailSegmentFields {
  CAMPAIGN = "campaign",
  AUTOMATION = "automation",
}
export enum ReceivedTextSegmentFields {
  CAMPAIGN = "campaign",
  AUTOMATION = "automation",
}
export enum ClickedTextSegmentFields {
  CAMPAIGN = "campaign",
  AUTOMATION = "automation",
}
export enum BouncedEmailSegmentFields {
  CAMPAIGN = "campaign",
  AUTOMATION = "automation",
  BOUNCE_TYPE = "bounce_type",
}

const marketingActivityDataStructure = createDataStructure(
  {
    campaign: {
      name: "Campaign",
      dataType: WhereConditionDataType.TOKEN,
      description: "The campaign that was received",
      category: SegmentConditionCategory.MARKETING,
    },
    automation: {
      name: "Automation",
      dataType: WhereConditionDataType.TOKEN,
      description: "The automation that was received",
      category: SegmentConditionCategory.MARKETING,
    },
  } satisfies Record<string, FieldConfig<string>>,
  CustomerDataSource.REDO,
);

const ReceivedEmailDataStructure = marketingActivityDataStructure;
const OpenedEmailDataStructure = marketingActivityDataStructure;
const ClickedEmailDataStructure = marketingActivityDataStructure;
const ReceivedTextDataStructure = marketingActivityDataStructure;
const ClickedTextDataStructure = marketingActivityDataStructure;

const BouncedEmailDataStructure = createDataStructure(
  {
    [BouncedEmailSegmentFields.CAMPAIGN]: {
      name: "Campaign",
      dataType: WhereConditionDataType.TOKEN,
      description: "The campaign that bounced",
      category: SegmentConditionCategory.MARKETING,
    },
    [BouncedEmailSegmentFields.AUTOMATION]: {
      name: "Automation",
      dataType: WhereConditionDataType.TOKEN,
      description: "The automation that bounced",
      category: SegmentConditionCategory.MARKETING,
    },
    [BouncedEmailSegmentFields.BOUNCE_TYPE]: {
      name: "Bounce type",
      dataType: WhereConditionDataType.TOKEN,
      description: "The type of bounce (Permanent, Transient, or Undetermined)",
      category: SegmentConditionCategory.MARKETING,
    },
  } satisfies Record<
    BouncedEmailSegmentFields,
    FieldConfig<BouncedEmailSegmentFields>
  >,
  CustomerDataSource.REDO,
);

export enum ReturnProcessedSegmentFields {
  PRODUCT_NAMES = "product_names",
  PRODUCT_VARIANT_NAMES = "product_variant_names",
  PRODUCT_SKUS = "product_skus",
  PRODUCT_ID = "product_id",
  PRODUCT_VARIANT_ID = "product_variant_id",
  COLLECTION_NAMES = "collection_names",
  PRODUCT_TAGS = "product_tags",
  RETURN_TYPE = "return_type",
}

const returnProcessedFields = {
  [ReturnProcessedSegmentFields.PRODUCT_NAMES]: {
    name: "Product names",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The names of the products on the processed return",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ReturnProcessedSegmentFields.PRODUCT_VARIANT_NAMES]: {
    name: "Product variant names",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The names of the product variants on the processed return",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ReturnProcessedSegmentFields.PRODUCT_SKUS]: {
    name: "Product SKUs",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The SKUs of the products on the processed return",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ReturnProcessedSegmentFields.PRODUCT_ID]: {
    name: "Product ID",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The Product IDs on the processed return",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ReturnProcessedSegmentFields.PRODUCT_VARIANT_ID]: {
    name: "Product Variant ID",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The Product variant IDs on the processed return",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ReturnProcessedSegmentFields.COLLECTION_NAMES]: {
    name: "Collection names",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description:
      "The names of the collections the products on the return belong to",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ReturnProcessedSegmentFields.PRODUCT_TAGS]: {
    name: "Product tags",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The tags on the products on the processed return",
    category: SegmentConditionCategory.SHOPPING,
  },
  [ReturnProcessedSegmentFields.RETURN_TYPE]: {
    name: "Return type",
    dataType: WhereConditionDataType.TOKEN,
    description:
      "The resolution for the returned product. (e.g. refund, exchange)",
    category: SegmentConditionCategory.SHOPPING,
  },
} satisfies Record<
  ReturnProcessedSegmentFields,
  FieldConfig<ReturnProcessedSegmentFields>
>;

const {
  shopify: ReturnProcessedShopifyDataStructure,
  commentSold: ReturnProcessedCommentSoldDataStructure,
} = createPlatformDataStructures(returnProcessedFields, {
  fromCommentSold: [
    ReturnProcessedSegmentFields.PRODUCT_VARIANT_NAMES,
    ReturnProcessedSegmentFields.RETURN_TYPE,
  ],
  fromBigCommerce: undefined,
});

export enum OrderPlacedSegmentFields {
  ORDER_TOTAL = "order_total",
  ITEM_COUNT = "item_count",
  COLLECTION_ID = "collection_id",
  COLLECTION_NAME = "collection_name",
  PRODUCT_ID = "product_id",
  PRODUCT_NAME = "product_name",
  PRODUCT_VARIANT_ID = "product_variant_id",
  PRODUCT_VARIANT_NAME = "product_variant_name",
  PRODUCT_VARIANT_SKU = "product_variant_sku",
  PRODUCT_TAG = "product_tags",
  VENDOR = "vendor",
}

const orderPlacedFields = {
  [OrderPlacedSegmentFields.ORDER_TOTAL]: {
    name: "Order total",
    dataType: WhereConditionDataType.NUMERIC,
    description: "The total value of the order",
    category: SegmentConditionCategory.SHOPPING,
    unit: "currency",
  },
  [OrderPlacedSegmentFields.ITEM_COUNT]: {
    name: "Item count",
    dataType: WhereConditionDataType.NUMERIC,
    description: "The number of items in the order",
    category: SegmentConditionCategory.SHOPPING,
  },
  [OrderPlacedSegmentFields.COLLECTION_ID]: {
    name: "Collection ids",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The ids of the collections the products belong to",
    category: SegmentConditionCategory.SHOPPING,
  },
  [OrderPlacedSegmentFields.COLLECTION_NAME]: {
    name: "Collection names",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The names of the collections the products belong to",
    category: SegmentConditionCategory.SHOPPING,
  },
  [OrderPlacedSegmentFields.PRODUCT_ID]: {
    name: "Product ids",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The ids of the products in the order",
    category: SegmentConditionCategory.SHOPPING,
  },
  [OrderPlacedSegmentFields.PRODUCT_NAME]: {
    name: "Product names",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The names of the products in the order",
    category: SegmentConditionCategory.SHOPPING,
  },
  [OrderPlacedSegmentFields.PRODUCT_VARIANT_ID]: {
    name: "Product variant ids",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The ids of the product variants in the order",
    category: SegmentConditionCategory.SHOPPING,
  },
  [OrderPlacedSegmentFields.PRODUCT_VARIANT_NAME]: {
    name: "Product variant names",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The names of the product variants in the order",
    category: SegmentConditionCategory.SHOPPING,
  },
  [OrderPlacedSegmentFields.PRODUCT_VARIANT_SKU]: {
    name: "Product variant SKUs",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The SKUs of the product variants in the order",
    category: SegmentConditionCategory.SHOPPING,
  },
  [OrderPlacedSegmentFields.PRODUCT_TAG]: {
    name: "Product tags",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The tags on the products in the order",
    category: SegmentConditionCategory.SHOPPING,
  },
  [OrderPlacedSegmentFields.VENDOR]: {
    name: "Vendor",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The vendors of the products in the order",
    category: SegmentConditionCategory.SHOPPING,
  },
} satisfies Record<
  OrderPlacedSegmentFields,
  FieldConfig<OrderPlacedSegmentFields>
>;

const {
  shopify: OrderPlacedShopifyDataStructure,
  commentSold: OrderPlacedCommentSoldDataStructure,
  bigCommerce: OrderPlacedBigCommerceDataStructure,
} = createPlatformDataStructures(orderPlacedFields, {
  fromCommentSold: [
    OrderPlacedSegmentFields.PRODUCT_VARIANT_NAME,
    OrderPlacedSegmentFields.VENDOR,
  ],
  // BigCommerce order events carry only totals and product/variant ids; product
  // names, collections, tags, and vendor are not available.
  fromBigCommerce: [
    OrderPlacedSegmentFields.COLLECTION_ID,
    OrderPlacedSegmentFields.COLLECTION_NAME,
    OrderPlacedSegmentFields.PRODUCT_ID,
    OrderPlacedSegmentFields.PRODUCT_NAME,
    OrderPlacedSegmentFields.PRODUCT_VARIANT_ID,
    OrderPlacedSegmentFields.PRODUCT_VARIANT_NAME,
    OrderPlacedSegmentFields.PRODUCT_VARIANT_SKU,
    OrderPlacedSegmentFields.PRODUCT_TAG,
    OrderPlacedSegmentFields.VENDOR,
  ],
});

export enum CheckoutStartedSegmentFields {
  PRODUCT_NAME = "product_name",
  PRODUCT_ID = "product_id",
  PRODUCT_VARIANT_ID = "product_variant_id",
  PRODUCT_VARIANT_NAME = "product_variant_name",
  PRODUCT_VARIANT_SKU = "product_variant_sku",
  PRODUCT_COLLECTION = "product_collection",
  PRODUCT_TAGS = "product_tags",
  QUANTITY = "quantity",
}

const checkoutStartedFields = {
  [CheckoutStartedSegmentFields.PRODUCT_NAME]: {
    name: "Product name",
    dataType: WhereConditionDataType.TOKEN,
    description: "The name of a product in the started checkout",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CheckoutStartedSegmentFields.PRODUCT_ID]: {
    name: "Product id",
    dataType: WhereConditionDataType.TOKEN,
    description: "The id of a product in the started checkout",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CheckoutStartedSegmentFields.PRODUCT_VARIANT_ID]: {
    name: "Product variant id",
    dataType: WhereConditionDataType.TOKEN,
    description: "The id of a product variant in the started checkout",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CheckoutStartedSegmentFields.PRODUCT_VARIANT_NAME]: {
    name: "Product variant name",
    dataType: WhereConditionDataType.TOKEN,
    description: "The name of a product variant in the started checkout",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CheckoutStartedSegmentFields.PRODUCT_VARIANT_SKU]: {
    name: "Product variant SKU",
    dataType: WhereConditionDataType.TOKEN,
    description: "The SKU of a product variant in the started checkout",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CheckoutStartedSegmentFields.PRODUCT_COLLECTION]: {
    name: "Product collections",
    dataType: WhereConditionDataType.TOKEN_LIST,
    description: "The collections a product in the started checkout belongs to",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CheckoutStartedSegmentFields.PRODUCT_TAGS]: commonProductFields.productTags(
    "in the started checkout",
  ),
  [CheckoutStartedSegmentFields.QUANTITY]: {
    name: "Item quantity",
    dataType: WhereConditionDataType.NUMERIC,
    description: "The quantity of a product variant in the started checkout",
    category: SegmentConditionCategory.SHOPPING,
  },
} satisfies Record<
  CheckoutStartedSegmentFields,
  FieldConfig<CheckoutStartedSegmentFields>
>;

const {
  shopify: CheckoutStartedShopifyDataStructure,
  commentSold: CheckoutStartedCommentSoldDataStructure,
  bigCommerce: CheckoutStartedBigCommerceDataStructure,
} = createPlatformDataStructures(checkoutStartedFields, {
  fromCommentSold: undefined,
  fromBigCommerce: [
    CheckoutStartedSegmentFields.PRODUCT_COLLECTION,
    CheckoutStartedSegmentFields.PRODUCT_TAGS,
  ],
});

export enum PaymentFailedSegmentFields {}

/** An activity the merchant can segment on but cannot filter within. */
const noFilterDataStructure = (
  source: CustomerDataSource,
): SegmentDataStructure<string> => ({ fields: {}, source });

export enum ActiveOnSiteSegmentFields {
  URL_WITHOUT_PARAMS = "urlWithoutParams",
}

const activeOnSiteFields = {
  [ActiveOnSiteSegmentFields.URL_WITHOUT_PARAMS]: {
    name: "URL",
    dataType: WhereConditionDataType.TOKEN,
    description:
      "The URL that was visited by the customer (excluding query parameters like ?utm_source=...)",
    category: SegmentConditionCategory.SHOPPING,
  },
} satisfies Record<
  ActiveOnSiteSegmentFields,
  FieldConfig<ActiveOnSiteSegmentFields>
>;

const {
  shopify: ActiveOnSiteShopifyDataStructure,
  commentSold: ActiveOnSiteCommentSoldDataStructure,
} = createPlatformDataStructures(activeOnSiteFields);

export enum SubscribedToBackInStockSegmentFields {
  PRODUCT_ID = "product_id",
  PRODUCT_NAME = "product_name",
  VARIANT_ID = "variant_id",
  PRODUCT_VARIANT_NAME = "product_variant_name",
  PRODUCT_VARIANT_SKU = "product_variant_sku",
  COLLECTION_NAME = "collection_name",
  PRODUCT_TAGS = "product_tags",
  VENDOR = "vendor",
}

const subscribedToBackInStockFields = {
  [SubscribedToBackInStockSegmentFields.PRODUCT_ID]:
    commonProductFields.productId("the customer subscribed to"),
  [SubscribedToBackInStockSegmentFields.PRODUCT_NAME]:
    commonProductFields.productName("the customer subscribed to"),
  [SubscribedToBackInStockSegmentFields.VARIANT_ID]:
    commonProductFields.variantId("the customer subscribed to"),
  [SubscribedToBackInStockSegmentFields.PRODUCT_VARIANT_NAME]:
    commonProductFields.variantName("the customer subscribed to"),
  [SubscribedToBackInStockSegmentFields.PRODUCT_VARIANT_SKU]:
    commonProductFields.variantSku("the customer subscribed to"),
  [SubscribedToBackInStockSegmentFields.COLLECTION_NAME]:
    commonProductFields.collectionName("that the product belongs to"),
  [SubscribedToBackInStockSegmentFields.PRODUCT_TAGS]:
    commonProductFields.productTags("the customer subscribed to"),
  [SubscribedToBackInStockSegmentFields.VENDOR]: commonProductFields.vendor(
    "the customer subscribed to",
  ),
} satisfies Record<
  SubscribedToBackInStockSegmentFields,
  FieldConfig<SubscribedToBackInStockSegmentFields>
>;

const {
  shopify: SubscribedToBackInStockShopifyDataStructure,
  commentSold: SubscribedToBackInStockCommentSoldDataStructure,
} = createPlatformDataStructures(subscribedToBackInStockFields, {
  fromCommentSold: [
    SubscribedToBackInStockSegmentFields.PRODUCT_VARIANT_NAME,
    SubscribedToBackInStockSegmentFields.VENDOR,
  ],
  fromBigCommerce: undefined,
});

export enum TriggeredBackInStockNotificationSegmentFields {
  PRODUCT_ID = "product_id",
  PRODUCT_NAME = "product_name",
  VARIANT_ID = "variant_id",
  PRODUCT_VARIANT_NAME = "product_variant_name",
  PRODUCT_VARIANT_SKU = "product_variant_sku",
  COLLECTION_NAME = "collection_name",
  PRODUCT_TAGS = "product_tags",
  VENDOR = "vendor",
}

const triggeredBackInStockNotificationFields = {
  [TriggeredBackInStockNotificationSegmentFields.PRODUCT_ID]:
    commonProductFields.productId("the customer was notified about"),
  [TriggeredBackInStockNotificationSegmentFields.PRODUCT_NAME]:
    commonProductFields.productName("the customer was notified about"),
  [TriggeredBackInStockNotificationSegmentFields.VARIANT_ID]:
    commonProductFields.variantId("the customer was notified about"),
  [TriggeredBackInStockNotificationSegmentFields.PRODUCT_VARIANT_NAME]:
    commonProductFields.variantName("the customer was notified about"),
  [TriggeredBackInStockNotificationSegmentFields.PRODUCT_VARIANT_SKU]:
    commonProductFields.variantSku("the customer was notified about"),
  [TriggeredBackInStockNotificationSegmentFields.COLLECTION_NAME]:
    commonProductFields.collectionName("that the product belongs to"),
  [TriggeredBackInStockNotificationSegmentFields.PRODUCT_TAGS]:
    commonProductFields.productTags("the customer was notified about"),
  [TriggeredBackInStockNotificationSegmentFields.VENDOR]:
    commonProductFields.vendor("the customer was notified about"),
} satisfies Record<
  TriggeredBackInStockNotificationSegmentFields,
  FieldConfig<TriggeredBackInStockNotificationSegmentFields>
>;

const {
  shopify: TriggeredBackInStockNotificationShopifyDataStructure,
  commentSold: TriggeredBackInStockNotificationCommentSoldDataStructure,
} = createPlatformDataStructures(triggeredBackInStockNotificationFields, {
  fromCommentSold: [
    TriggeredBackInStockNotificationSegmentFields.PRODUCT_VARIANT_NAME,
    TriggeredBackInStockNotificationSegmentFields.VENDOR,
  ],
  fromBigCommerce: undefined,
});

/**
 * Keys a merged structure ends up with: shared fields keep their name, the
 * rest gain the platform they came from.
 */
type MergedFieldKey<S extends string, C extends string, A extends string> =
  | Extract<S | C, A>
  | `${Exclude<S, A>}_shopify`
  | `${Exclude<C, A>}_commentsold`;

/** Pass `platformAgnosticFields` as const, or the shared keys widen away. */
function mergeDataStructuresWithPlatformLabels<
  S extends string,
  C extends string,
  const A extends string = never,
>(
  shopifyStructure: SegmentDataStructure<S>,
  commentSoldStructure: SegmentDataStructure<C>,
  platformAgnosticFields?: readonly A[],
): SegmentDataStructure<MergedFieldKey<S, C, A>> {
  type Key = MergedFieldKey<S, C, A>;
  const mergedFields = {} as Record<Key, SegmentDataStructureField<Key>>;
  const platformAgnosticSet = new Set<string>(platformAgnosticFields ?? []);
  // Whether a field is shared is decided by the caller list, so both
  // checks are stated here rather than asserted at each use.
  const isShared = (key: S | C): key is Extract<S | C, A> =>
    platformAgnosticSet.has(key);
  const isPlatformSpecific = <T extends string>(key: T): key is Exclude<T, A> =>
    !platformAgnosticSet.has(key);

  for (const [key, field] of objectEntries(shopifyStructure.fields)) {
    const typedField = field as SegmentDataStructureField<Key>;
    if (isPlatformSpecific(key)) {
      const newKey = `${key}_shopify` as const;
      mergedFields[newKey] = {
        ...typedField,
        name: `${typedField.name} (Shopify)`,
        type: newKey,
        baseFieldName: key,
      };
    } else if (isShared(key)) {
      mergedFields[key] = typedField;
    }
  }

  for (const [key, field] of objectEntries(commentSoldStructure.fields)) {
    const typedField = field as SegmentDataStructureField<Key>;
    if (isPlatformSpecific(key)) {
      const newKey = `${key}_commentsold` as const;
      mergedFields[newKey] = {
        ...typedField,
        name: `${typedField.name} (CommentSold)`,
        type: newKey,
        baseFieldName: key,
      };
    }
  }

  return { fields: mergedFields, source: CustomerDataSource.REDO };
}

export enum MarketingSubscribedSegmentFields {
  ENTRY_POINT = "entry_point",
  CHANNEL = "channel",
}

const MarketingSubscribedDataStructure = createDataStructure(
  {
    [MarketingSubscribedSegmentFields.ENTRY_POINT]: {
      name: "Entry point",
      dataType: WhereConditionDataType.TOKEN,
      description: "The entry point through which the customer subscribed",
      category: SegmentConditionCategory.MARKETING,
    },
    [MarketingSubscribedSegmentFields.CHANNEL]: {
      name: "Channel",
      dataType: WhereConditionDataType.TOKEN,
      description:
        "The marketing channel the customer subscribed to (email or SMS)",
      category: SegmentConditionCategory.MARKETING,
    },
  } satisfies Record<
    MarketingSubscribedSegmentFields,
    FieldConfig<MarketingSubscribedSegmentFields>
  >,
  CustomerDataSource.REDO,
);

export const segmentDataStructureTypeToDataStructure = {
  [CUSTOMER_CHARACTERISTIC_DATA_STRUCTURE]: CustomerAttributeDataStructure,
  [CustomerActivityType.VIEWED_PRODUCT_SHOPIFY]:
    ProductViewedShopifyDataStructure,
  [CustomerActivityType.VIEWED_PRODUCT_COMMENTSOLD]:
    ProductViewedCommentSoldDataStructure,
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_SHOPIFY]:
    ProductAddedToCartShopifyDataStructure,
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_COMMENTSOLD]:
    ProductAddedToCartCommentSoldDataStructure,
  [CustomerActivityType.CHECKOUT_STARTED_SHOPIFY]:
    CheckoutStartedShopifyDataStructure,
  [CustomerActivityType.CHECKOUT_STARTED_COMMENTSOLD]:
    CheckoutStartedCommentSoldDataStructure,
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_BIGCOMMERCE]:
    ProductAddedToCartBigCommerceDataStructure,
  [CustomerActivityType.CHECKOUT_STARTED_BIGCOMMERCE]:
    CheckoutStartedBigCommerceDataStructure,
  [CustomerActivityType.ORDER_PLACED_BIGCOMMERCE]:
    OrderPlacedBigCommerceDataStructure,
  [CustomerActivityType.ORDER_PLACED_SHOPIFY]: OrderPlacedShopifyDataStructure,
  [CustomerActivityType.ORDER_PLACED_COMMENTSOLD]:
    OrderPlacedCommentSoldDataStructure,
  [CustomerActivityType.RECEIVED_EMAIL]: ReceivedEmailDataStructure,
  [CustomerActivityType.OPENED_EMAIL]: OpenedEmailDataStructure,
  [CustomerActivityType.CLICKED_EMAIL]: ClickedEmailDataStructure,
  [CustomerActivityType.RECEIVED_TEXT]: ReceivedTextDataStructure,
  [CustomerActivityType.CLICKED_TEXT]: ClickedTextDataStructure,
  [CustomerActivityType.ACTIVE_ON_SITE_SHOPIFY]:
    ActiveOnSiteShopifyDataStructure,
  [CustomerActivityType.ACTIVE_ON_SITE_COMMENTSOLD]:
    ActiveOnSiteCommentSoldDataStructure,
  [CustomerActivityType.COLLECTION_VIEWED_SHOPIFY]:
    CollectionViewedShopifyDataStructure,
  [CustomerActivityType.PAYMENT_FAILED_NO_ORDER_CREATED_SHOPIFY]:
    noFilterDataStructure(CustomerDataSource.SHOPIFY),
  [CustomerActivityType.PAYMENT_FAILED_AFTER_ORDER_CREATED_SHOPIFY]:
    noFilterDataStructure(CustomerDataSource.SHOPIFY),
  [CustomerActivityType.COLLECTION_VIEWED_COMMENTSOLD]:
    CollectionViewedCommentSoldDataStructure,
  [CustomerActivityType.RETURN_PROCESSED]:
    mergeDataStructuresWithPlatformLabels(
      ReturnProcessedShopifyDataStructure,
      ReturnProcessedCommentSoldDataStructure,
      [ReturnProcessedSegmentFields.RETURN_TYPE],
    ),
  [CustomerActivityType.SUBSCRIBED_TO_BACK_IN_STOCK]:
    mergeDataStructuresWithPlatformLabels(
      SubscribedToBackInStockShopifyDataStructure,
      SubscribedToBackInStockCommentSoldDataStructure,
    ),
  [CustomerActivityType.TRIGGERED_BACK_IN_STOCK_NOTIFICATION]:
    mergeDataStructuresWithPlatformLabels(
      TriggeredBackInStockNotificationShopifyDataStructure,
      TriggeredBackInStockNotificationCommentSoldDataStructure,
    ),
  [CustomerActivityType.BOUNCED_EMAIL]: BouncedEmailDataStructure,
  [CustomerActivityType.REPLIED_SMS]: noFilterDataStructure(
    CustomerDataSource.REDO,
  ),
  [CustomerActivityType.MARKETING_SUBSCRIBED]: MarketingSubscribedDataStructure,
  [CustomerActivityType.MARKETING_CONFIRMED]: noFilterDataStructure(
    CustomerDataSource.REDO,
  ),
} satisfies Record<SegmentDataStructureType, SegmentDataStructure<string>>;

const segmentFieldIndex = new Map(
  Object.entries(segmentDataStructureTypeToDataStructure).map(
    ([dataStructure, structure]) =>
      [
        dataStructure,
        new Map<string, SegmentDataStructureField<string>>(
          Object.entries(structure.fields),
        ),
      ] as const,
  ),
);

/** Look up a field whose name is only known at runtime. */
export function findSegmentField(
  dataStructure: SegmentDataStructureType,
  fieldName: string,
): SegmentDataStructureField<string> | undefined {
  return segmentFieldIndex.get(dataStructure)?.get(fieldName);
}
